import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Truck, ArrowLeft, CheckCircle } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, {
        email,
        origin_url: window.location.origin
      });
      setSent(true);
      toast.success("Reset link sent if account exists");
    } catch (err) {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="forgot-password-page" className="min-h-[80vh] flex items-center justify-center bg-[#F9F8F6] px-4 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-12 h-12 bg-[#E68A00] rounded-md flex items-center justify-center mx-auto mb-4">
            <Truck className="w-6 h-6 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-[#1A2F24]" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Reset Password
          </h1>
          <p className="text-sm text-[#595959] mt-1">Enter your email to receive a reset link</p>
        </div>

        {sent ? (
          <div className="bg-white border border-[#E5E0D8] rounded-lg p-8 text-center">
            <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-7 h-7 text-green-600" />
            </div>
            <h2 className="text-lg font-semibold text-[#1A2F24] mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Check Your Email
            </h2>
            <p className="text-sm text-[#595959] mb-6">
              If an account exists with that email, we've sent a password reset link. Check your inbox (and spam folder).
            </p>
            <Link to="/login" className="text-[#E68A00] font-medium text-sm hover:underline" data-testid="back-to-login-link">
              <ArrowLeft className="w-4 h-4 inline mr-1" />Back to Login
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="bg-white border border-[#E5E0D8] rounded-lg p-6 space-y-4">
            {error && (
              <div data-testid="forgot-pw-error" className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-md">
                {error}
              </div>
            )}
            <div>
              <Label htmlFor="reset-email" className="text-[#1C1C1C]">Email Address</Label>
              <Input
                id="reset-email"
                data-testid="forgot-pw-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                className="mt-1.5 bg-white border-[#E5E0D8] focus:ring-[#E68A00]"
              />
            </div>
            <button
              type="submit"
              data-testid="forgot-pw-submit"
              disabled={loading}
              className="w-full bg-[#E68A00] hover:bg-[#CC7A00] disabled:opacity-60 text-white font-semibold py-3 rounded-md transition-colors"
            >
              {loading ? "Sending..." : "Send Reset Link"}
            </button>
            <p className="text-center text-sm text-[#595959]">
              <Link to="/login" className="text-[#E68A00] font-medium hover:underline">
                <ArrowLeft className="w-3.5 h-3.5 inline mr-1" />Back to Login
              </Link>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
